import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeIcdDocId } from './diagnosisCodeService';

/**
 * Validate ICD exists in diagnosisCodes and assignable === true. Returns category label for UI/denormalization.
 */
export async function validateAssignableDiagnosisCode(icdRaw) {
  const id = normalizeIcdDocId(icdRaw);
  if (!id) return { ok: false, error: 'ICD code is required.' };
  const snap = await getDoc(doc(db, 'diagnosisCodes', id));
  if (!snap.exists()) {
    return { ok: false, error: `Unknown ICD code: ${id}. Use a code from Billing Setup → Diagnosis codes.` };
  }
  const d = snap.data();
  if (d.assignable !== true) {
    return {
      ok: false,
      error: `ICD ${id} is not assignable (only assignable codes may be attached to a patient).`,
    };
  }
  const category =
    d.category != null && String(d.category).trim()
      ? String(d.category).trim()
      : d.shortDesc != null && String(d.shortDesc).trim()
        ? String(d.shortDesc).trim()
        : null;
  return { ok: true, icdCode: id, category };
}

export function newDiagnosisEntry({ icdCode, category, dxEffectiveDate, createdByUid }) {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `dx_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  return {
    id,
    icdCode: normalizeIcdDocId(icdCode),
    category: category ?? null,
    dxEffectiveDate,
    dxTerminatedDate: null,
    createdAt: new Date().toISOString(),
    createdByUid: createdByUid || null,
    terminatedAt: null,
    terminatedByUid: null,
  };
}

export function terminateDiagnosisEntry(entry, { dxTerminatedDate, terminatedByUid }) {
  return {
    ...entry,
    dxTerminatedDate,
    terminatedAt: new Date().toISOString(),
    terminatedByUid: terminatedByUid || null,
  };
}

/** Compare YYYY-MM-DD strings (or date-compatible). */
export function compareIsoDates(a, b) {
  if (!a || !b) return 0;
  return String(a).localeCompare(String(b));
}

export function isDiagnosisActiveAsOf(entry, todayStr) {
  if (!entry || entry.dxTerminatedDate) return false;
  const eff = entry.dxEffectiveDate;
  if (!eff) return false;
  return compareIsoDates(eff, todayStr) <= 0;
}

/**
 * Date-aware status for reports: active on `ymd` when effective on/before it and
 * not yet terminated (a termination dated `ymd` ends the diagnosis that day).
 */
export function isDiagnosisActiveOnDate(entry, ymd) {
  if (!entry || !ymd) return false;
  const eff = entry.dxEffectiveDate;
  if (!eff || compareIsoDates(eff, ymd) > 0) return false;
  const term = entry.dxTerminatedDate;
  return !term || compareIsoDates(term, ymd) > 0;
}

/** Active on at least one day of [startYmd, endYmd]. */
export function isDiagnosisActiveDuringPeriod(entry, startYmd, endYmd) {
  if (!entry || !startYmd || !endYmd) return false;
  const eff = entry.dxEffectiveDate;
  if (!eff || compareIsoDates(eff, endYmd) > 0) return false;
  const term = entry.dxTerminatedDate;
  return !term || compareIsoDates(term, startYmd) > 0;
}

export function isDiagnosisFuture(entry, todayStr) {
  if (!entry || entry.dxTerminatedDate) return false;
  const eff = entry.dxEffectiveDate;
  if (!eff) return false;
  return compareIsoDates(eff, todayStr) > 0;
}

export function partitionDiagnosesForDisplay(diagnoses, todayStr) {
  const list = Array.isArray(diagnoses) ? [...diagnoses] : [];
  const active = [];
  const pending = [];
  const terminated = [];
  for (const row of list) {
    if (row.dxTerminatedDate) {
      terminated.push(row);
    } else if (isDiagnosisFuture(row, todayStr)) {
      pending.push(row);
    } else if (isDiagnosisActiveAsOf(row, todayStr)) {
      active.push(row);
    } else {
      pending.push(row);
    }
  }
  const sortByEffective = (a, b) => compareIsoDates(a.dxEffectiveDate, b.dxEffectiveDate);
  active.sort(sortByEffective);
  pending.sort(sortByEffective);
  terminated.sort((a, b) => {
    const t = compareIsoDates(a.dxTerminatedDate, b.dxTerminatedDate);
    return t !== 0 ? t : compareIsoDates(a.dxEffectiveDate, b.dxEffectiveDate);
  });
  return { active, pending, terminated };
}
